import { z } from 'zod'

import { SCOPE_RULES } from '@ems/storage/scopes'

import { api } from '#admin/lib/api-client.ts'

const presignSchema = z.object({
  url: z.string(),
  key: z.string(),
  headers: z.record(z.string(), z.string()),
  expiresInSeconds: z.number(),
})

export type UploadScope =
  | 'company-logo'
  | 'profile-document'
  | 'cms-media'
  | 'speaker-photo'
  | 'tenant-branding'
  | 'payment-proof'
  | 'venue-plan'

export type UploadLimits = { accept: string; maxBytes: number; kinds: string }

const TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/webp': 'WebP',
  'image/avif': 'AVIF',
  'image/gif': 'GIF',
  'image/svg+xml': 'SVG',
  'image/x-icon': 'ICO',
}

function describeTypes(types: readonly string[]): string {
  const labels = types.map((type) => TYPE_LABELS[type] ?? type)
  const last = labels.pop()
  const joined = labels.length === 0 ? (last ?? '') : `${labels.join(', ')} or ${last ?? ''}`
  const images = types.every((type) => type.startsWith('image/'))
  return `a ${joined}${images ? ' image' : ''}`
}

function limitsOf(types: readonly string[], maxBytes: number): UploadLimits {
  return { accept: types.join(','), maxBytes, kinds: describeTypes(types) }
}

function scopeLimits(scope: UploadScope, only?: (type: string) => boolean): UploadLimits {
  const rules = SCOPE_RULES[scope]
  const types = only === undefined ? rules.contentTypes : rules.contentTypes.filter(only)
  return limitsOf(types, rules.maxBytes)
}

export const UPLOAD_LIMITS: Record<UploadScope, UploadLimits> = {
  'company-logo': scopeLimits('company-logo'),
  'profile-document': scopeLimits('profile-document'),
  'cms-media': scopeLimits('cms-media'),
  'speaker-photo': scopeLimits('speaker-photo'),
  'payment-proof': scopeLimits('payment-proof'),
  'venue-plan': scopeLimits('venue-plan'),
  // The scope also takes SVG and ICO, for a favicon. The invoice PDF embeds only PNG and JPEG.
  'tenant-branding': scopeLimits('tenant-branding', (type) =>
    ['image/png', 'image/jpeg'].includes(type),
  ),
}

/** The favicon takes the whole branding scope: a tab draws SVG and ICO, which the PDF cannot. */
export const FAVICON_LIMITS: UploadLimits = scopeLimits('tenant-branding')

export const CMS_IMAGE_LIMITS: UploadLimits = scopeLimits('cms-media', (type) =>
  type.startsWith('image/'),
)

export class UploadRefused extends Error {}

/**
 * Upload one file and return its storage **key** — never a URL, which would be a capability with
 * an expiry baked into the data.
 *
 * Two steps: the API presigns (choosing the key from the edition and the scope, never from
 * anything sent here), then the file goes straight to storage. The size and type are checked
 * here first so the refusal is a sentence rather than a failed request.
 */
export async function uploadFile(
  scope: UploadScope,
  file: File,
  limits: UploadLimits = UPLOAD_LIMITS[scope],
): Promise<string> {
  if (!limits.accept.split(',').includes(file.type)) {
    throw new UploadRefused(`Upload ${limits.kinds}.`)
  }
  if (file.size > limits.maxBytes) {
    throw new UploadRefused(`That file is over ${Math.round(limits.maxBytes / 1_000_000)} MB.`)
  }

  const request = { filename: file.name, contentType: file.type, contentLength: file.size }
  const signed =
    scope === 'cms-media'
      ? await api.post('/v1/cms/admin/media/presign', { schema: presignSchema, body: request })
      : await api.post('/v1/uploads/presign', {
          schema: presignSchema,
          body: { scope, ...request },
        })

  const response = await fetch(signed.url, { method: 'PUT', headers: signed.headers, body: file })
  if (!response.ok) throw new UploadRefused('The upload did not finish. Try again.')
  return signed.key
}

/** `tenants/…/profile-document/…/ktp.pdf` → `ktp.pdf`. What a person recognizes their file by. */
export function fileNameOf(key: string): string {
  return key.split('/').at(-1) ?? key
}
